import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, GripVertical, ChevronDown, ChevronUp, Loader2, Settings, Search, Plus } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import {
  COMPONENT_AVAILABLE_METRICS,
  COMPONENT_METRICS_LIMITS,
  GOOGLE_ADS_METRICS,
  getComponentMetrics,
  saveComponentMetrics,
  getMetricLabel,
  getMetricApiKey,
  getAdvancedOptionsForComponent,
  hasAdvancedOptions,
  getPlatformFromComponentId,
  isMetricRatio,
  getRatioMetricConfig,
  getAllMetricObjectives,
  saveAllMetricObjectives,
  getTemplatesForComponent,
  applyTemplateToAllComponents,
  getAllGlobalLabels,
  saveAllGlobalLabels
} from '../utils/metricsUtils';

/**
 * MetricsConfigModal - Modal de configuration des métriques par composant
 * Layout simple : liste unique avec drag & drop intégré et boutons de configuration
 */
const MetricsConfigModal = ({
  isOpen,
  onClose,
  componentId,
  accountId = 'global',
  componentTitle = 'Composant',
  hideApplyToAll = false,
  token = null,
  propertyId = null,
  currentAdvancedConfig = null,
  onAdvancedConfigSave = null
}) => {
  // État local pour les métriques sélectionnées et leur ordre
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [applyToAll, setApplyToAll] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedAvailableMetric, setDraggedAvailableMetric] = useState(null);

  // État pour la métrique dont la config est ouverte
  const [expandedMetric, setExpandedMetric] = useState(null);

  // État pour les labels personnalisés de toutes les métriques
  const [customLabels, setCustomLabels] = useState({});

  // État pour les objectifs par métrique { [metricKey]: { value: number|null, enabled: boolean } }
  const [metricObjectives, setMetricObjectives] = useState({});

  // États pour les options avancées (conversions)
  const [availableEvents, setAvailableEvents] = useState([]);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  /**
   * Les événements comptés comme conversion — **plusieurs**, et non un seul.
   *
   * Une propriété distingue souvent « demande de devis », « rappel formulaire » et « contact
   * bouton » : les compter séparément oblige à lire trois cartes pour connaître le volume de
   * contacts, et aucune n'est celle qui intéresse. Le serveur acceptait déjà une liste —
   * `conversionEvents` est un tableau, et le filtre GA4 un `inListFilter` — seule l'interface
   * la réduisait à son premier élément.
   *
   * `['transactions']` est la **valeur sentinelle** du comportement standard : le serveur la
   * reconnaît et bascule sur la métrique GA4 `conversions`, les événements clés de la propriété.
   * Elle est donc exclusive — la mêler à des événements nommés compterait deux fois ce qui est
   * à la fois clé et choisi.
   */
  const [selectedEvents, setSelectedEvents] = useState(['transactions']);

  /**
   * La pré-sélection par défaut est-elle **provisoire** ?
   *
   * ## Le défaut observé
   *
   * Chez Meta, `purchase` est coché à l'ouverture quand rien n'a été configuré — c'est le
   * comportement historique de la colonne. Mais c'est un défaut, pas un choix : l'utilisateur
   * qui coche ensuite deux conversions personnalisées s'attend à compter **ces deux-là**, et
   * se retrouvait avec trois. L'achat restait coché, hors de la zone visible de la liste, et
   * seul l'intitulé « Conversions (3 événements) » le trahissait.
   *
   * La sentinelle des plateformes Analytics n'avait pas ce problème : exclusive, elle
   * disparaissait d'elle-même au premier choix. Ici le plancher est un type d'action ordinaire,
   * qu'aucune règle ne retire — il fallait donc le marquer comme provisoire.
   *
   * ⚠️ **Un `['purchase']` enregistré volontairement n'est pas provisoire** : on peut alors
   * vouloir compter les achats *et* les prospects, et le second clic doit cumuler. D'où le
   * besoin de savoir si la configuration vient du compte ou du défaut, et non de la seule
   * valeur — les deux sont identiques.
   */
  const [defautProvisoire, setDefautProvisoire] = useState(false);

  /**
   * Les conversions retenues **absentes du relevé**, et qui gardent leur ligne.
   *
   * ⚠️ **Cet ensemble ne se vide jamais tant que la fenêtre est ouverte**, et c'est tout son
   * intérêt. La liste était d'abord dérivée de la sélection courante : décocher « Achat » le
   * retirait de la sélection, donc de la liste, donc de l'écran — une case qui s'évapore
   * quand on l'actionne, et qu'on ne peut plus recocher si l'on s'est trompé.
   *
   * On mémorise donc ce qu'on a eu à montrer, pas ce qui est coché à l'instant. La ligne reste
   * en place, décochée, jusqu'à la fermeture.
   */
  const [orphelinesAffichees, setOrphelinesAffichees] = useState([]);
  const [saving, setSaving] = useState(false);

  // État initial pour détecter les changements de config conversion
  const [initialConversionConfig, setInitialConversionConfig] = useState(null);

  // État pour la recherche dans les métriques disponibles
  const [searchQuery, setSearchQuery] = useState('');

  // Limites et métriques disponibles
  const maxMetrics = COMPONENT_METRICS_LIMITS[componentId] || 10;
  const availableMetricKeys = COMPONENT_AVAILABLE_METRICS[componentId] || [];

  // Options avancées pour ce composant
  const advancedOptions = useMemo(() => getAdvancedOptionsForComponent(componentId), [componentId]);
  const hasAdvanced = hasAdvancedOptions(componentId);

  // Templates disponibles pour ce composant
  const templates = useMemo(() => getTemplatesForComponent(componentId), [componentId]);

  // État pour afficher le feedback après application d'un template
  const [templateApplied, setTemplateApplied] = useState(null);

  // Métriques non sélectionnées filtrées par la recherche
  const filteredAvailableMetrics = useMemo(() => {
    const unselected = availableMetricKeys.filter(key => !selectedMetrics.includes(key));
    if (!searchQuery.trim()) return unselected;

    const query = searchQuery.toLowerCase().trim();
    return unselected.filter(key => {
      const label = getMetricLabel(key, componentId).toLowerCase();
      const apiKey = getMetricApiKey(key, componentId).toLowerCase();
      return label.includes(query) || apiKey.includes(query) || key.toLowerCase().includes(query);
    });
  }, [availableMetricKeys, selectedMetrics, searchQuery, componentId]);

  // Déterminer la plateforme à partir du componentId
  const getPlatformFromComponent = (compId) => {
    if (compId.startsWith('fbads_')) return 'fbads';
    if (compId.startsWith('msads_')) return 'msads';
    if (compId.startsWith('ga4_')) return 'ga4';
    if (compId.startsWith('piano_')) return 'piano';
    if (compId.startsWith('matomo_')) return 'matomo';
    if (compId.startsWith('tiktok_')) return 'tiktok';
    if (compId.startsWith('pinterest_')) return 'pinterest';
    if (compId.startsWith('chatgpt_')) return 'chatgpt';
    if (compId.startsWith('awin_')) return 'awin';
    return 'gads'; // Défaut Google Ads
  };

  // Appliquer un template à tout le dashboard
  const applyTemplate = (template) => {
    const confirmed = window.confirm(
      `Appliquer le template "${template.name}" à tout le dashboard ?\n\nCette action va modifier les métriques de tous les composants.`
    );
    if (!confirmed) return;

    // Appliquer à tout le dashboard
    const platform = getPlatformFromComponent(componentId);
    applyTemplateToAllComponents(template, accountId, platform);

    // Mettre à jour l'affichage local pour ce composant
    const isResultsComponent = componentId === 'results' || componentId.endsWith('_results');
    const templateMetrics = isResultsComponent ? template.metrics : (template.metricsExpanded || template.metrics);
    const filteredMetrics = templateMetrics.filter(m => availableMetricKeys.includes(m));
    const limitedMetrics = filteredMetrics.slice(0, maxMetrics);
    setSelectedMetrics(limitedMetrics);
    setExpandedMetric(null);

    // Afficher le feedback puis fermer le modal
    setTemplateApplied(template.name);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  // Charger la configuration actuelle à l'ouverture
  useEffect(() => {
    if (isOpen) {
      const cfg = getComponentMetrics(componentId, accountId);
      // Filtrer les métriques sauvegardées pour ne garder que celles disponibles
      const savedMetrics = cfg.order || cfg.metrics || [];
      const filteredMetrics = savedMetrics.filter(m => availableMetricKeys.includes(m));
      setSelectedMetrics(filteredMetrics);

      // Charger les labels personnalisés GLOBAUX (par plateforme)
      const platform = getPlatformFromComponent(componentId);
      setCustomLabels(getAllGlobalLabels(accountId, platform));
      setApplyToAll(false);
      setExpandedMetric(null);
      setTemplateApplied(null);

      // Charger les objectifs de métriques GLOBAUX (par plateforme)
      const objectives = getAllMetricObjectives(componentId, accountId);
      setMetricObjectives(objectives);

      /*
       * Le réglage d'origine diffère selon la plateforme.
       *
       * Chez Meta, c'est `purchase` — un type d'action nommé, qui doit apparaître **coché**
       * dans la liste. Partir de la sentinelle y laisserait l'écran sans aucune case cochée,
       * et le groupe « Par défaut » qui l'aurait portée n'y est pas affiché : l'utilisateur
       * aurait vu une métrique qui ne compte rien.
       *
       * ⚠️ Une configuration enregistrée avant ce changement peut contenir la sentinelle. On
       * la traduit à la lecture plutôt que de migrer la base : le serveur la comprend déjà
       * comme « les achats », et une migration pour un littéral n'en vaut pas le risque.
       */
      // Même valeur que `PLANCHER`, recalculée ici : cet effet s'exécute au montage, avant
      // que le corps du composant n'ait produit la constante.
      const plancherPlateforme = componentId?.startsWith('fbads_') ? 'purchase' : 'transactions';
      const enregistres = currentAdvancedConfig?.conversionEvents?.length
        ? currentAdvancedConfig.conversionEvents
        : [];
      const initialEvents = enregistres.length
        ? enregistres.map(e => (e === 'transactions' ? plancherPlateforme : e))
        : [plancherPlateforme];
      setDefautProvisoire(enregistres.length === 0);
      setOrphelinesAffichees([]);
      const initialLabel = currentAdvancedConfig?.conversionLabel || '';
      setSelectedEvents(initialEvents);

      // Stocker la config initiale pour détecter les changements. La liste est comparée par sa
      // forme triée et jointe : l'ordre de sélection n'est pas une modification.
      setInitialConversionConfig({
        events: [...initialEvents].sort().join('|'),
        label: initialLabel
      });
    }
  }, [isOpen, componentId, accountId, currentAdvancedConfig]);

  // Charger les événements disponibles quand on ouvre la config d'une métrique avec options avancées
  useEffect(() => {
    if (expandedMetric && hasAdvanced && advancedOptions[expandedMetric] && token && propertyId && availableEvents.length === 0 && !advancedLoading) {
      loadAvailableEvents();
    }
  }, [expandedMetric]);

  /*
   * Repérer les retenues sans ligne, dès que le catalogue ou la sélection change.
   *
   * On ajoute, on ne retire jamais : voir `orphelinesAffichees`. Le garde `nouvelles.length`
   * évite de reposer le même tableau à chaque rendu, ce qui relancerait l'effet sans fin.
   */
  useEffect(() => {
    if (availableEvents.length === 0) return;
    const connus = new Set(availableEvents.map(e => e.eventName));
    const nouvelles = selectedEvents.filter(
      nom => nom !== 'transactions' && !connus.has(nom) && !orphelinesAffichees.includes(nom)
    );
    if (nouvelles.length > 0) {
      setOrphelinesAffichees(precedentes => [...precedentes, ...nouvelles]);
    }
  }, [availableEvents, selectedEvents, orphelinesAffichees]);

  // Charger les événements disponibles depuis l'API
  const loadAvailableEvents = async () => {
    if (!token || !propertyId || !advancedOptions[expandedMetric]) return;

    setAdvancedLoading(true);
    try {
      const response = await axios.get(
        `${config.API_URL}${advancedOptions[expandedMetric].loadEndpoint}/${propertyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { period: 'LAST_30_DAYS' }
        }
      );
      if (response.data.success) {
        setAvailableEvents(response.data.events || []);
        if (response.data.currentConfig) {
          const servis = response.data.currentConfig.conversionEvents || [];
          setSelectedEvents(servis.length
            ? servis.map(e => (e === SENTINELLE ? PLANCHER : e))
            : [PLANCHER]);
          /*
           * `configuree` vient du serveur et dit si le compte porte une configuration à lui.
           * On ne peut pas le déduire de la valeur : un `['purchase']` enregistré et le défaut
           * se ressemblent exactement, et seul le premier doit cumuler au clic suivant.
           */
          if (response.data.configuree === false) setDefautProvisoire(true);
          else if (response.data.configuree === true) setDefautProvisoire(false);
          if (response.data.currentConfig.conversionLabel) {
            setCustomLabels(prev => ({ ...prev, conv: response.data.currentConfig.conversionLabel }));
          }
        }
      }
    } catch (err) {
      console.error('Erreur chargement événements disponibles:', err);
    } finally {
      setAdvancedLoading(false);
    }
  };

  // Gestion sélection/désélection d'une métrique
  const toggleMetric = (metricKey) => {
    if (selectedMetrics.includes(metricKey)) {
      setSelectedMetrics(prev => prev.filter(m => m !== metricKey));
      if (expandedMetric === metricKey) {
        setExpandedMetric(null);
      }
    } else if (selectedMetrics.length < maxMetrics) {
      setSelectedMetrics(prev => [...prev, metricKey]);
    }
  };

  // Toggle la config d'une métrique
  const toggleMetricConfig = (metricKey) => {
    setExpandedMetric(expandedMetric === metricKey ? null : metricKey);
  };

  // Gestion drag & drop (uniquement sur les métriques sélectionnées)
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newOrder = [...selectedMetrics];
    const [dragged] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, dragged);
    setSelectedMetrics(newOrder);
    setDraggedIndex(targetIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Gestion drag & drop depuis la colonne droite (métriques disponibles)
  const handleAvailableDragStart = (e, metricKey) => {
    setDraggedAvailableMetric(metricKey);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', metricKey);
  };

  const handleAvailableDragEnd = () => {
    setDraggedAvailableMetric(null);
  };

  // Gestion du drop sur la zone des métriques actives
  const handleDropOnActiveZone = (e) => {
    e.preventDefault();
    if (draggedAvailableMetric && selectedMetrics.length < maxMetrics && !selectedMetrics.includes(draggedAvailableMetric)) {
      setSelectedMetrics(prev => [...prev, draggedAvailableMetric]);
    }
    setDraggedAvailableMetric(null);
  };

  const handleDragOverActiveZone = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  /**
   * ⚠️ **Meta n'a pas de mode « par défaut » : il a un type d'action nommé.**
   *
   * Chez Google Analytics, Piano et Matomo, le réglage d'origine est un *mode* serveur — les
   * événements clés, les transactions, tous les objectifs — qui ne porte aucun nom
   * d'événement : d'où la sentinelle `transactions` et son bouton radio, exclusif par nature.
   *
   * Chez Meta, le réglage d'origine est `purchase`, un type d'action comme les autres. En
   * faire un bouton radio le rendait **incumulable** : impossible de compter les achats *et*
   * les prospects, alors que c'est exactement ce que demande un compte mixte. Il redevient
   * donc une case à cocher ordinaire, cochée par défaut, et le groupe « Par défaut »
   * disparaît de cet écran — il n'y a rien à y mettre.
   *
   * `PLANCHER` est ce vers quoi l'on retombe en décochant la dernière case : une liste vide ne
   * compterait rien, et un écran de conversions sans conversion n'est pas un état qu'on veut
   * pouvoir enregistrer.
   */
  const estMeta = getPlatformFromComponent(componentId) === 'fbads';
  /*
   * Les noms français des types retenus, joints par le serveur à la configuration. Ils servent
   * à nommer une conversion **absente du catalogue** : elle n'a pas de ligne d'où tirer son
   * libellé, et son identifiant technique seul ne dirait pas ce qu'elle compte.
   */
  const conversionLabels = currentAdvancedConfig?.libelles || null;
  const SENTINELLE = 'transactions';
  const PLANCHER = estMeta ? 'purchase' : SENTINELLE;

  /**
   * Cocher ou décocher un événement.
   *
   * `transactions` reste exclusif dans les deux sens : le choisir vide la liste des événements
   * nommés, et en cocher un l'en retire. Tout décocher y revient — une liste vide ne compterait
   * rien, et un écran de conversions sans conversion n'est pas un état qu'on veut pouvoir
   * enregistrer.
   */
  const handleEventChange = (eventName) => {
    if (eventName === SENTINELLE) {
      setSelectedEvents([SENTINELLE]);
      return;
    }
    setSelectedEvents(precedents => {
      // Le premier choix explicite **remplace** la pré-sélection par défaut : cocher deux
      // conversions doit en compter deux, pas trois.
      const base = defautProvisoire ? [] : precedents.filter(e => e !== SENTINELLE);
      const suivants = base.includes(eventName)
        ? base.filter(e => e !== eventName)
        : [...base, eventName];
      return suivants.length > 0 ? suivants : [PLANCHER];
    });
    setDefautProvisoire(false);
  };

  // Gestion du changement de label pour une métrique
  const handleLabelChange = (metricKey, value) => {
    setCustomLabels(prev => ({
      ...prev,
      [metricKey]: value
    }));
  };

  // Gestion du changement de valeur d'objectif pour une métrique
  // L'objectif est automatiquement activé si une valeur est saisie
  const handleObjectiveValueChange = (metricKey, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    setMetricObjectives(prev => ({
      ...prev,
      [metricKey]: {
        ...prev[metricKey],
        value: numValue,
        enabled: numValue !== null && !isNaN(numValue)
      }
    }));
  };

  // Obtenir le label affiché pour une métrique
  /**
   * Composant analytics ? GA4, Piano, Matomo.
   *
   * La plateforme se déduit du `componentId` par `getPlatformFromComponent`, la fonction que ce
   * modal emploie déjà pour les templates et les libellés : une seconde règle écrite à la main
   * finit toujours par diverger d'elle. C'est arrivé — `componentId === 'results'` figurait ici, pris pour
   * le tableau de synthèse. Or `results` est l'identifiant de la carte Résultats **de Google
   * Ads** : la carte analytique porte `ga4_results`, et les autres régies leur propre préfixe.
   * Le modal de Google Ads annonçait donc « % Conversion (événements clés) » et la formule
   * « événements clés / Sessions », deux notions qui n'existent que dans Google Analytics.
   *
   * Calculé une fois ici plutôt qu'à l'intérieur de `getRatioFormula` : le libellé de
   * `taux_conv` en dépend aussi.
   */
  const estAnalytics = ['ga4', 'piano', 'matomo'].includes(getPlatformFromComponent(componentId));

  /**
   * Les plateformes dont la métrique Conversions **nomme ce qu'elle compte**.
   *
   * Meta s'y ajoute sans être « analytics » : sa colonne ne comptait que les achats, elle est
   * désormais configurable, et l'intitulé doit suivre le réglage comme ailleurs. Mais la
   * formule d'un ratio Meta n'a rien de commun avec celle d'un ratio Analytics — « / Sessions »
   * n'y veut rien dire —, d'où deux prédicats plutôt qu'un seul élargi.
   */
  const conversionConfigurable = estAnalytics || estMeta;

  /**
   * Le nom lisible de l'événement compté comme conversion.
   *
   * `selectedEvents` porte les noms techniques (`add_to_cart`), `availableEvents` leur libellé
   * français (« Ajout au panier »). On rend le second quand on l'a, le premier sinon —
   * afficher `add_to_cart` reste plus utile que de ne rien afficher.
   */
  const libelleEvenementConversion = () => {
    // `transactions` est la valeur sentinelle du réglage « comportement standard ». Elle ne
    // veut pas dire que le numérateur est le nombre d'achats : le serveur bascule alors sur la
    // métrique GA4 `conversions`, c'est-à-dire les événements clés. Écrire « transactions »
    // ici rendrait cette carte indistinguable de `taux_transaction`, qui compte, elle, de vrais
    // achats.
    // Un nom saisi à la main l'emporte : sans cela, la métrique Conversions afficherait
    // « Paniers » pendant que le taux d'à côté afficherait « Ajout au panier ».
    const nomSaisi = (customLabels.conv || '').trim();
    if (nomSaisi && nomSaisi !== 'Conversions') return nomSaisi;
    // Chez Meta la sentinelle vaut « les achats » : « événements clés » y renverrait à une
    // notion de Google Analytics que Meta ne connaît pas.
    if (selectedEvents.length === 0 || selectedEvents.includes('transactions')) {
      return estMeta ? 'achats' : 'événements clés';
    }

    const nommer = (cle) => availableEvents.find((e) => e.eventName === cle)?.label || cle;
    // Au-delà de deux, l'énumération tiendrait sur deux lignes dans un intitulé de carte : on
    // nomme le nombre, le détail se lisant dans la fenêtre de configuration.
    /*
     * ⚠️ **Une seule source : son nom. Deux ou plus : leur nombre.**
     *
     * Même règle que dans les en-têtes de colonne (`libelleEvenementConversion` de
     * `metricsUtils`), et c'est le but : l'intitulé lu dans cette fenêtre doit être exactement
     * celui qui s'affichera au-dessus de la colonne. Les énumérer tenait sur sept lignes dès
     * deux conversions personnalisées, dont les noms techniques font quarante caractères.
     *
     * Meta parle de conversions là où Google Analytics parle d'événements ; « sources » les
     * recouvre tous les deux sans emprunter le vocabulaire de l'autre.
     */
    if (selectedEvents.length >= 2) return `${selectedEvents.length} sources`;
    return nommer(selectedEvents[0]);
  };

  /**
   * Le libellé affiché d'une métrique.
   *
   * `taux_conv` est le seul à dépendre d'un réglage : son numérateur est l'événement choisi
   * sous la métrique Conversions. L'appeler « Taux de Conversion » tout court le rendait
   * indistinguable de `taux_transaction`, qui s'affichait « % de conversion » — deux cartes
   * voisines, deux numérateurs différents, et aucun moyen de les démêler à l'écran.
   *
   * Un nom choisi à la main par l'utilisateur l'emporte toujours.
   */
  const libelleParDefaut = (metricKey) => {
    if (conversionConfigurable && metricKey === 'taux_conv') {
      return `% Conversion (${libelleEvenementConversion()})`;
    }
    if (conversionConfigurable && metricKey === 'conv') {
      return `Conversions (${libelleEvenementConversion()})`;
    }
    return getMetricLabel(metricKey, componentId);
  };

  /**
   * Le nom retenu : celui que l'utilisateur a choisi, sinon le défaut ci-dessus.
   *
   * Nuance sur `conv` : le serveur substitue la chaîne `'Conversions'` comme repli à une
   * quinzaine d'endroits, si bien qu'elle arrive même quand l'utilisateur n'a jamais rien
   * saisi. La prendre pour un choix masquerait la parenthèse qu'on vient d'ajouter — on ne
   * la retient donc pas.
   */
  const getDisplayLabel = (metricKey) => {
    const nomChoisi = (customLabels[metricKey] || '').trim();
    if (nomChoisi && !(metricKey === 'conv' && nomChoisi === 'Conversions')) return nomChoisi;
    return libelleParDefaut(metricKey);
  };

  // Définitions des formules pour les métriques ratio
  const RATIO_FORMULAS = {
    taux_conv: {
      // Sur un composant analytics, le numérateur n'est pas « Conversions » en général mais
      // l'événement précis qu'on a choisi de compter. L'écrire évite de lire la même formule
      // sous deux métriques qui ne mesurent pas la même chose.
      formula: (labels, isAnalytics) => (isAnalytics
        ? `${libelleEvenementConversion()} / ${labels.sessions}`
        : `${labels.conv} / ${labels.clicks}`),
      components: ['conv', 'sessions', 'clicks']
    },
    // `taux_transaction` n'avait aucune formule : sa carte restait muette, alors que sa
    // voisine affichait la sienne. Le numérateur est fixe — les achats — et ne dépend
    // d'aucun réglage.
    taux_transaction: {
      formula: (labels) => `${labels.transactions} / ${labels.sessions}`,
      components: ['transactions', 'sessions']
    },
    panier_moyen: {
      formula: (labels) => `${labels.valeur_conv} / ${labels.conv}`,
      components: ['valeur_conv', 'conv']
    },
    cpa: {
      formula: (labels) => `${labels.cout} / ${labels.conv}`,
      components: ['cout', 'conv']
    },
    cos: {
      formula: (labels) => `${labels.cout} / ${labels.valeur_conv} × 100`,
      components: ['cout', 'valeur_conv']
    },
    roas: {
      formula: (labels) => `(${labels.valeur_conv} / ${labels.cout}) × 100`,
      components: ['valeur_conv', 'cout']
    },
    roi: {
      formula: (labels) => `(${labels.valeur_conv} - ${labels.cout}) / ${labels.cout}`,
      components: ['valeur_conv', 'cout']
    },
    ctr: {
      formula: (labels) => `${labels.clicks} / ${labels.impressions} × 100`,
      components: ['clicks', 'impressions']
    },
    cpc: {
      formula: (labels) => `${labels.cout} / ${labels.clicks}`,
      components: ['cout', 'clicks']
    },
    cpm: {
      formula: (labels) => `${labels.cout} / ${labels.impressions} × 1000`,
      components: ['cout', 'impressions']
    },
    cpv: {
      formula: (labels) => `${labels.cout} / ${labels.video_views}`,
      components: ['cout', 'video_views']
    },
    vtr: {
      formula: (labels) => `${labels.video_views} / ${labels.impressions} × 100`,
      components: ['video_views', 'impressions']
    },
    rebond: {
      formula: (labels) => `Rebonds / ${labels.sessions} × 100`,
      components: ['sessions']
    },
    pages_visite: {
      formula: (labels) => `${labels.pageviews} / ${labels.sessions}`,
      components: ['pageviews', 'sessions']
    },
    duree_moy: {
      formula: (labels) => `Durée totale / ${labels.sessions}`,
      components: ['sessions']
    },
    taux_engagement: {
      formula: (labels) => `Engagements / ${labels.impressions} × 100`,
      components: ['impressions']
    },
    cout_par_interaction: {
      formula: (labels) => `${labels.cout} / Interactions`,
      components: ['cout']
    },
    cout_par_reaction: {
      formula: (labels) => `${labels.cout} / Réactions`,
      components: ['cout']
    }
  };

  // Obtenir la formule d'un ratio avec les labels personnalisés
  const getRatioFormula = (metricKey, useCustomLabels = true) => {
    const formulaConfig = RATIO_FORMULAS[metricKey];
    if (!formulaConfig) return null;

    // Construire l'objet des labels pour tous les composants potentiels
    const getLabel = useCustomLabels ? getDisplayLabel : (key) => getMetricLabel(key, componentId);
    const labels = {
      conv: getLabel('conv'),
      valeur_conv: getLabel('valeur_conv'),
      cout: getLabel('cout'),
      clicks: getLabel('clicks'),
      impressions: getLabel('impressions'),
      sessions: getLabel('sessions'),
      transactions: getLabel('transactions'),
      pageviews: getLabel('pageviews'),
      video_views: getLabel('video_views')
    };

    return formulaConfig.formula(labels, estAnalytics);
  };

  // Sauvegarde
  const handleSave = async () => {
    // Demander si l'utilisateur veut appliquer à tous les composants (sauf si hideApplyToAll)
    let shouldApplyToAll = false;
    if (!hideApplyToAll) {
      shouldApplyToAll = window.confirm(
        'Voulez-vous appliquer cette configuration à tous les composants du même type ?\n\n' +
        '• Cliquez sur "OK" pour appliquer à tous\n' +
        '• Cliquez sur "Annuler" pour appliquer uniquement à ce composant'
      );
    }

    setSaving(true);

    try {
      // Sauvegarder les métriques (sans les labels, ils sont maintenant globaux)
      saveComponentMetrics(
        componentId,
        selectedMetrics,
        selectedMetrics,
        accountId,
        shouldApplyToAll,
        {} // Les labels sont maintenant globaux
      );

      // Sauvegarder les labels personnalisés GLOBAUX (par plateforme)
      const platform = getPlatformFromComponent(componentId);
      saveAllGlobalLabels(customLabels, accountId, platform);

      // Sauvegarder les objectifs de métriques GLOBAUX (par plateforme)
      saveAllMetricObjectives(componentId, accountId, metricObjectives);

      /**
       * Les options avancées des conversions (GA4), si elles ont changé.
       *
       * La condition portait aussi sur `selectedMetrics.includes('conv')`, ce qui liait
       * l'enregistrement du réglage à la présence de la métrique dans le tableau. Deux cas
       * s'en trouvaient perdus, maintenant que le rouage est aussi du côté des métriques
       * disponibles : celui où l'on règle l'événement sans activer Conversions, et celui où
       * `% Conversion (<événement>)` est seule active — elle compte pourtant le même
       * événement, et son nom en dépend.
       *
       * `hasConfigChanged` suffit à ne rien écrire quand rien n'a bougé.
       */
      if (hasAdvanced && onAdvancedConfigSave && advancedOptions.conv) {
        const convLabel = customLabels.conv || getMetricLabel('conv', componentId);
        const currentLabel = convLabel.trim() || 'Conversions';

        // Vérifier si la config a changé
        const hasConfigChanged =
          [...selectedEvents].sort().join('|') !== initialConversionConfig?.events ||
          currentLabel !== initialConversionConfig?.label;

        if (hasConfigChanged) {
          try {
            const response = await axios.post(
              `${config.API_URL}${advancedOptions.conv.apiEndpoint}`,
              {
                propertyId,
                conversionEvents: selectedEvents,
                conversionLabel: currentLabel,
                /*
                 * Les noms lisibles des types retenus, envoyés **avec** le choix.
                 *
                 * Une conversion personnalisée Meta — « Réservation » — ne figure dans aucune
                 * table : son nom vit chez Meta, et cette fenêtre est le seul endroit où on
                 * l'a sous les yeux. Sans cet envoi, l'en-tête de colonne afficherait
                 * `offsite_conversion.custom.1418277969749982`, et il faudrait rappeler Meta
                 * à chaque chargement du dashboard pour un simple intitulé.
                 */
                conversionLabels: Object.fromEntries(
                  selectedEvents
                    .map(nom => [nom, availableEvents.find(e => e.eventName === nom)?.label])
                    .filter(([, label]) => Boolean(label))
                )
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (response.data.success) {
              onAdvancedConfigSave(response.data.config || {
                conversionEvents: selectedEvents,
                conversionLabel: currentLabel
              });
            }
          } catch (err) {
            console.error('Erreur sauvegarde config avancée:', err);
          }
        }
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Grouper les événements par catégorie
  /**
   * Ce que « Par défaut » compte, solution par solution.
   *
   * La valeur stockée est la même partout — le littéral `transactions` — pour que le stockage
   * et les gardes restent une seule mécanique. ⚠️ Mais **elle ne désigne pas la même mesure**,
   * et le texte doit donc changer : le dashboard Piano affichait « Événements clés, marqués
   * comme tels dans GA4 », ce que Piano ne connaît pas.
   */
  const optionParDefaut = useMemo(() => {
    switch (getPlatformFromComponentId(componentId)) {
      case 'piano':
        return { nom: 'Transactions', precision: 'les ventes e-commerce du site' };
      case 'matomo':
        return { nom: 'Tous les objectifs', precision: 'les conversions, objectifs confondus' };
      /*
       * Meta n'a pas de notion d'« événement clé ». Son défaut historique — et celui que la
       * colonne affichait avant d'être configurable — ce sont les achats, quel que soit le nom
       * sous lequel le pixel les remonte.
       */
      case 'fbads':
        return { nom: 'Achats', precision: 'quel que soit le nom du type chez Meta' };
      default:
        return { nom: 'Événements clés', precision: 'marqués comme tels dans GA4' };
    }
  }, [componentId]);

  const eventsByCategory = useMemo(() => {
    const categories = {
      ecommerce: { label: 'E-commerce', events: [] },
      lead: { label: 'Génération de leads', events: [] },
      form: { label: 'Formulaires', events: [] },
      engagement: { label: 'Engagement', events: [] },
      session: { label: 'Sessions', events: [] },
      // Matomo range ses conversions sous des objectifs, pas des événements.
      goal: { label: 'Objectifs', events: [] },
      /*
       * Deux familles propres à Meta.
       *
       * Les ranger sous « Événements personnalisés » aurait mélangé un clic de lien avec une
       * conversion personnalisée du compte — or c'est précisément cette distinction que
       * l'utilisateur vient chercher ici.
       */
      trafic: { label: 'Trafic', events: [] },
      messagerie: { label: 'Messagerie', events: [] },
      custom: { label: 'Événements personnalisés', events: [] }
    };

    availableEvents.forEach(event => {
      const category = event.category || 'custom';
      if (categories[category]) {
        categories[category].events.push(event);
      } else {
        categories.custom.events.push(event);
      }
    });

    const groupes = Object.entries(categories)
      .filter(([_, cat]) => cat.events.length > 0)
      .map(([key, cat]) => ({ key, ...cat }));

    /*
     * ⚠️ **Une conversion retenue mais absente du relevé doit quand même avoir sa ligne.**
     *
     * Le catalogue est bâti sur ce que le compte a **réellement servi** : un type jamais
     * déclenché n'y figure pas. Or il peut parfaitement être retenu — par le réglage par
     * défaut (`purchase` sur un compte qui ne vend pas), par une configuration enregistrée
     * quand le type était encore actif, ou par un pixel qui a cessé d'émettre.
     *
     * Sans ligne, il devenait **invisible et indécochable** : l'intitulé annonçait « 3
     * conversions » pour deux cases cochées, et rien à l'écran n'expliquait la troisième.
     * Mesuré sur un compte du parc : `purchase` retenu par défaut, aucun achat servi en
     * 90 jours, deux conversions personnalisées cochées — trois annoncées.
     *
     * Le groupe est placé **en tête**, et non à sa place alphabétique : c'est précisément ce
     * qu'on ne trouvait pas en faisant défiler la liste.
     */
    const connus = new Set(availableEvents.map(e => e.eventName));
    const orphelins = orphelinesAffichees
      .filter(nom => !connus.has(nom))
      .map(nom => ({
        eventName: nom,
        label: conversionLabels?.[nom] || nom,
        eventCount: 0,
        note: "Aucun événement de ce type sur les 90 derniers jours : cochée, elle n'ajoute rien."
      }));

    /*
     * ⚠️ **Ce que le compte sert passe avant ce qu'il pourrait servir.**
     *
     * Meta propose ses dix-sept événements standard même à zéro, pour qu'un compte qui ne vend
     * pas encore puisse quand même retenir « Achat ». Mais rangés par famille, ces zéros
     * occupaient tout le haut de la liste : sur un compte réel, les deux conversions qui
     * tournent — 35 et 14 — se retrouvaient sous dix-sept lignes vides, hors de l'écran.
     *
     * Deux tris, donc : les groupes qui ont du volume d'abord, à ordre de déclaration constant
     * entre eux ; puis, dans chaque groupe, le volume décroissant. Sur les plateformes dont
     * tous les événements ont un volume — Google Analytics, Piano, Matomo — ces deux tris ne
     * changent rien.
     */
    const aDuVolume = (groupe) => groupe.events.some(e => (e.eventCount || 0) > 0);
    for (const groupe of groupes) {
      groupe.events = [...groupe.events].sort((a, b) => (b.eventCount || 0) - (a.eventCount || 0));
    }
    const ranges = [...groupes.filter(aDuVolume), ...groupes.filter(g => !aDuVolume(g))];

    /*
     * Les retenues sans ligne restent **épinglées en tête**, hors du tri par volume.
     *
     * Elles sont à zéro par définition — c'est ce qui les rend orphelines — et le tri
     * ci-dessus les renverrait tout en bas, c'est-à-dire exactement là où on ne les trouvait
     * pas. Ce groupe n'apparaît que pour une conversion retenue qu'aucune ligne ne représente :
     * une conversion personnalisée supprimée chez Meta, ou un type hors de la liste standard
     * qui a cessé d'émettre.
     */
    if (orphelins.length > 0) {
      ranges.unshift({ key: 'absentes', label: 'Retenues, mais absentes du relevé', events: orphelins });
    }
    return ranges;
  }, [availableEvents, orphelinesAffichees, conversionLabels]);

  /**
   * Le panneau de réglages d'une métrique : nom affiché, objectif, événement compté.
   *
   * Partagé par les deux colonnes. Il ne vivait que du côté des métriques actives, si bien
   * qu'il fallait ajouter « Conversions » au tableau pour seulement choisir l'événement
   * qu'elle compte — puis la retirer si l'on ne la voulait pas. Le réglage et la sélection
   * sont deux gestes distincts ; les lier n'avait pas de raison d'être.
   */
  const panneauDeConfiguration = (metricKey) => (
    <div className="metric-config-panel">
      {/* Nom personnalisé (pour toutes les métriques) */}
      <div className="config-field">
        <label>Nom affiché</label>
        <input
          type="text"
          value={customLabels[metricKey] || ''}
          onChange={(e) => handleLabelChange(metricKey, e.target.value)}
          placeholder={getMetricLabel(metricKey, componentId)}
        />
      </div>

      {/* Objectif (uniquement pour les métriques ratios) */}
      {isMetricRatio(metricKey) && (() => {
        const ratioConfig = getRatioMetricConfig(metricKey);
        const objective = metricObjectives[metricKey] || { value: null, enabled: false };
        return (
          <div className="config-field config-field-objective">
            <label>
              Objectif
              <span className="objective-hint">
                (active la coloration)
              </span>
            </label>
            <div className="objective-input-row">
              <input
                type="number"
                step="any"
                value={objective.value ?? ''}
                onChange={(e) => handleObjectiveValueChange(metricKey, e.target.value)}
                placeholder="Ex: 2.5"
                className="objective-value-input"
              />
              <span className="objective-unit">{ratioConfig.unit}</span>
            </div>
            <div className="objective-logic-hint">
              {ratioConfig.lowerIsBetter
                ? `Vert si ≤ ${objective.value ?? '?'}${ratioConfig.unit}`
                : `Vert si ≥ ${objective.value ?? '?'}${ratioConfig.unit}`
              }
            </div>
          </div>
        );
      })()}

      {/* Sélection d'événement (uniquement pour conv avec options avancées) */}
      {metricKey === 'conv' && hasAdvanced && advancedOptions.conv && (
        <div className="config-field">
          <label>{estMeta ? 'Conversions à compter' : 'Événement à compter'}</label>
          {/*
            * ⚠️ **Les volumes affichés ne sont pas ceux du dashboard.**
            *
            * Ce recensement porte sur 90 jours — il faut cette profondeur pour qu'une
            * conversion rare apparaisse —, alors que le dashboard affiche la période choisie
            * en haut de l'écran. Deux cases à 1 peuvent donc donner 0 sur sept jours, ou 3
            * sur six mois. Sans cette ligne, l'écart se lit comme une erreur de calcul.
            */}
          {estMeta && (
            <div className="config-field-hint">
              Volumes relevés sur les 90 derniers jours ; le dashboard, lui, affiche la période
              que vous avez choisie.
            </div>
          )}
          {advancedLoading ? (
            <div className="config-loading">
              <Loader2 size={18} className="spin" />
              <span>Chargement...</span>
            </div>
          ) : (
            <div className="events-select-list">
              {/*
                Le retour au comportement par défaut.

                La liste ne proposait que les événements réels de la propriété : une fois
                `view_item` choisi, plus rien ne ramenait aux événements clés — une porte à
                sens unique, alors que c'est le réglage d'origine.

                `transactions` est la valeur sentinelle qu'attend le serveur : `useTransactions`
                la reconnaît et bascule sur la métrique GA4 `conversions`, c'est-à-dire ce que
                la propriété a marqué comme événement clé. Un sous-ensemble, parfois vide,
                jamais « tous les événements » — mesuré sur une propriété : 1 conversion pour
                4 234 événements, 1 événement clé sur 17.
              */}
              {!estMeta && (
              <div className="event-group">
                <div className="event-group-label">Par défaut</div>
                <label
                  className={`event-option ${selectedEvents.includes('transactions') ? 'selected' : ''}`}
                >
                  {/* Un bouton radio, et non une case : ce choix exclut les autres. */}
                  <input
                    type="radio"
                    name="conversionEvent"
                    checked={selectedEvents.includes('transactions')}
                    onChange={() => handleEventChange('transactions')}
                  />
                  <span className="event-option-name">{optionParDefaut.nom}</span>
                  <span className="event-option-hint">{optionParDefaut.precision}</span>
                </label>
              </div>
              )}
              {eventsByCategory.map(category => (
                <div key={category.key} className="event-group">
                  <div className="event-group-label">{category.label}</div>
                  {category.events.map(event => (
                    <label
                      key={event.eventName}
                      className={`event-option ${selectedEvents.includes(event.eventName) ? 'selected' : ''}`}
                    >
                      {/* Des cases à cocher : plusieurs événements peuvent nourrir la même
                          mesure — « demande de devis » et « rappel formulaire » sont deux
                          chemins vers le même contact. */}
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(event.eventName)}
                        onChange={() => handleEventChange(event.eventName)}
                      />
                      <span className="event-option-name">
                        {event.label || event.eventName}
                        {/*
                          * ⚠️ **Les autres noms sous lesquels Meta sert la même conversion.**
                          *
                          * Meta rend un achat sous cinq noms — `purchase`, `omni_purchase`,
                          * `offsite_conversion.fb_pixel_purchase`… — tous à la même valeur. La
                          * liste n'en propose qu'un, sans quoi en cocher deux doublerait le
                          * total. L'afficher n'est pas un détail : c'est ce qui explique que
                          * les autres n'apparaissent pas, et évite qu'on les cherche.
                          */}
                        {event.aussiServiComme?.length > 0 && (
                          <span className="event-option-aliases">
                            aussi servi sous {event.aussiServiComme.join(', ')}
                          </span>
                        )}
                        {event.note && (
                          <span className="event-option-note">{event.note}</span>
                        )}
                      </span>
                      {event.eventCount !== undefined && (
                        <span className="event-option-count">
                          {event.eventCount.toLocaleString('fr-FR')}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>

  );

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay metrics-config-overlay" onClick={onClose}>
      <div className="modal metrics-config-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Configurer les métriques - {componentTitle}</h2>
          <button className="modal-close-btn" onClick={onClose} title="Fermer">
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Templates rapides */}
          {templates && templates.length > 0 && (
            <div className="metrics-templates-section">
              <div className="metrics-templates-label">Templates rapides (appliqués à tout le dashboard) :</div>
              <div className="metrics-templates-buttons">
                {templates.map(template => (
                  <button
                    key={template.id}
                    className="metrics-template-btn"
                    onClick={() => applyTemplate(template)}
                    title={template.description}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
              {templateApplied && (
                <div className="template-applied-feedback">
                  <Check size={14} />
                  Template "{templateApplied}" appliqué à tout le dashboard
                </div>
              )}
            </div>
          )}

          {/* Layout 2 colonnes */}
          <div className="metrics-two-columns">
            {/* Colonne gauche : Métriques sélectionnées */}
            <div className="metrics-column-left">
              <div className="metrics-column-header">
                <h4>Métriques actives <span className="metrics-count">({selectedMetrics.length}/{maxMetrics})</span></h4>
              </div>

              <div
                className={`metrics-drop-zone ${draggedAvailableMetric ? 'drag-over' : ''}`}
                onDrop={handleDropOnActiveZone}
                onDragOver={handleDragOverActiveZone}
              >
                {selectedMetrics.length === 0 ? (
                  <div className="metrics-empty-message">
                    <Plus size={24} className="empty-icon" />
                    <div>Ajoutez des métriques depuis la liste de droite</div>
                  </div>
                ) : (
                  <div className="metrics-unified-list">
                    {/* Métriques sélectionnées (ordonnées, avec drag and drop) */}
                    {selectedMetrics.map((metricKey, index) => {
              const isExpanded = expandedMetric === metricKey;
              const isDragging = draggedIndex === index;

              return (
                <div key={metricKey} className="metric-unified-item-wrapper">
                  <div
                    className={`metric-unified-item selected ${isDragging ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Poignée drag */}
                    <div className="drag-handle-area active">
                      <GripVertical size={16} className="drag-icon" />
                    </div>

                    {/* Checkbox + Label */}
                    <label className="metric-main-label">
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => toggleMetric(metricKey)}
                      />
                      <div className="metric-info">
                        <span className="metric-name">{getDisplayLabel(metricKey)}</span>
                        {isMetricRatio(metricKey) && getRatioFormula(metricKey) && (
                          <span className="metric-api">{getRatioFormula(metricKey)}</span>
                        )}
                      </div>
                    </label>

                    {/* Bouton config (toujours visible pour renommer) */}
                    <button
                      className={`metric-config-toggle ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleMetricConfig(metricKey)}
                      title="Configurer cette métrique"
                    >
                      <Settings size={16} />
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>

                  {/* Panneau de réglages, partagé avec la colonne des disponibles. */}
                  {isExpanded && panneauDeConfiguration(metricKey)}
                </div>
              );
            })}
                  </div>
                )}
              </div>
            </div>

            {/* Colonne droite : Métriques disponibles */}
            <div className="metrics-column-right">
              <div className="metrics-column-header">
                <h4>Métriques disponibles</h4>
              </div>

              {/* Zone de recherche */}
              <div className="metrics-search-box">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une métrique..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="clear-search" onClick={() => setSearchQuery('')}>
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Liste scrollable des métriques disponibles */}
              <div className="metrics-scrollable-list">
                {filteredAvailableMetrics.length === 0 ? (
                  <div className="metrics-no-results">
                    {searchQuery ? `Aucune métrique trouvée pour "${searchQuery}"` : 'Toutes les métriques sont déjà actives'}
                  </div>
                ) : (
                  filteredAvailableMetrics.map(metricKey => {
                    const isDisabled = selectedMetrics.length >= maxMetrics;

                    return (
                      /* Le panneau de réglages est **frère** de la ligne, et non son enfant :
                         la ligne porte un `onClick` qui bascule la métrique, et le moindre
                         clic dans le panneau l'aurait activée. */
                      <div key={metricKey} className="metric-available-item-wrapper">
                      <div
                        className={`metric-available-item ${isDisabled ? 'disabled' : ''}`}
                        draggable={!isDisabled}
                        onDragStart={(e) => !isDisabled && handleAvailableDragStart(e, metricKey)}
                        onDragEnd={handleAvailableDragEnd}
                        onClick={() => !isDisabled && toggleMetric(metricKey)}
                      >
                        <GripVertical size={14} className="drag-hint" />
                        {/*
                          Case à cocher, décochée par construction : cette colonne ne contient
                          que des métriques inactives. La cocher revient à activer la métrique,
                          comme un clic sur la ligne ou le glisser-déposer. Un bouton
                          « Ajouter » faisait un quatrième geste pour la même action, à côté
                          d'une case dont c'était déjà le rôle.

                          `stopPropagation` sur le label est indispensable : la ligne entière
                          porte déjà un `onClick` qui bascule la métrique. Sans lui, un clic sur
                          la case déclencherait la bascule deux fois — ajout puis retrait — et la
                          case paraîtrait ne rien faire.
                        */}
                        <label
                          className="metric-main-label"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && toggleMetric(metricKey)}
                          />
                          <div className="metric-info">
                            {/* `getDisplayLabel` et non `libelleParDefaut` : cette colonne
                                ignorait le nom personnalisé, au motif qu'elle montre ce qu'est
                                la métrique et non comment on l'a nommée. Le rouage rend ce
                                nom modifiable ici même : ne pas en montrer l'effet ferait
                                croire que le champ ne fait rien. */}
                            <span className="metric-name">{getDisplayLabel(metricKey)}</span>
                            {isMetricRatio(metricKey) && getRatioFormula(metricKey, false) && (
                              <span className="metric-api">{getRatioFormula(metricKey, false)}</span>
                            )}
                          </div>
                        </label>
                        {/* Même rouage que dans la colonne des actives : régler une métrique
                            et l'activer sont deux gestes distincts. Il fallait auparavant
                            ajouter « Conversions » au tableau pour seulement choisir
                            l'événement qu'elle compte, puis la retirer si on n'en voulait pas. */}
                        <button
                          className={`metric-config-toggle ${expandedMetric === metricKey ? 'expanded' : ''}`}
                          onClick={(e) => { e.stopPropagation(); toggleMetricConfig(metricKey); }}
                          title="Configurer cette métrique"
                        >
                          <Settings size={16} />
                          {expandedMetric === metricKey ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                      {expandedMetric === metricKey && panneauDeConfiguration(metricKey)}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={selectedMetrics.length === 0 || saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Check size={16} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MetricsConfigModal;
